/**
 * The session-key prompt — the first step of `<TradingSetupModal>`.
 *
 * Copy is `docs/07` §7 verbatim:
 *
 * > `Enable one-click trading` / `One signature covers 7 days. This key can
 * > place and cancel orders — it can never move your money.`
 *
 * The second sentence is the whole product argument for D3 and it is not
 * marketing: a user is about to authorise a key they cannot see, and the bound
 * on what it can do is the only thing that makes that reasonable. It is stated
 * before the signature, not in a help article.
 *
 * **It said "it can never withdraw" until §F28n.** That was true of a venue
 * holding the funds, and it now understates the case in a way that reads as
 * hedging: there is no withdrawal endpoint for a key to be refused, because the
 * funds sit in the user's own wallet. "Never move your money" is the promise the
 * architecture actually makes, and it is a promise about **this key** — which is
 * the only thing this modal is asking about.
 *
 * **B8 narrowed what surrounds it, and the difference matters.** The venue now
 * settles in USDU, its own 1:1 wrapper over USDC (`contracts/src/USDU.sol`), and
 * that contract carries admin `block` / `unblock` behind an upgradeable proxy:
 * the exchange multi-sig **can** freeze an account, and could move a balance by
 * upgrading the token (it could reassign a frozen one directly until `docs/06`
 * §F124 retired `redistribute`). So the sentence
 * above is no longer the whole custody story, and it must not be read as one.
 * It stays word for word because it is still exactly true of a session key, and
 * the fourth bullet below is what stops it being the only thing said. The
 * account-level power is stated in full where a user acts on it — `/money`'s
 * deposit panel, before they wrap anything — and in `docs/07`.
 *
 * **The TTL comes from `config.session_keys.key_ttl_ms`, not from the copy.**
 * `docs/07` once said "7 days" while `openapi`'s config example shipped one day
 * — so rendering a literal string would tell the user something false the
 * moment those disagree. The number is formatted from config and the sentence
 * is built around it (docs/06 §F21j). Since §F75 it is the venue's own choice
 * of lifetime rather than a default the client proposed.
 */

import { formatTtl } from '@/lib/format'
import type { IdentityState } from '@/wallet/identity'
import { isRollBoundary } from '@/wallet/rotation'

export interface SessionKeyStepProps {
  identity: IdentityState
  /** `config.session_keys.key_ttl_ms`. */
  ttlMs: number
  /** For the roll warning. Omitted outside the market page. */
  market?: { anchorS: number; periodS: number; nowMs: number }
}

/**
 * The session-key step of `<TradingSetupModal>` — the body this file's modal
 * used to render on its own, before the funding steps joined it in one flow.
 */
export function SessionKeyStep({ identity, ttlMs, market }: SessionKeyStepProps) {
  // The user asked for this, so it is never blocked — `mayAutoRotate`'s roll
  // guard governs *automatic* rotation only. Refusing a signature someone just
  // clicked for, because the clock says T+58, is the client second-guessing an
  // explicit instruction. Telling them what they are walking into is the right
  // amount of help.
  const atBoundary =
    market ?
      isRollBoundary({
        keyExpiresAtMs: null,
        nowMs: market.nowMs,
        anchorS: market.anchorS,
        periodS: market.periodS,
      })
    : false

  return (
    <>
      <p className="text-body text-text-primary">
        One signature covers {formatTtl(ttlMs)}. This key can place and cancel
        orders — it can never move your money.
      </p>

      <ul className="mt-3 space-y-1_5 text-caption text-text-secondary">
        <li>The key is generated in this browser and never leaves it.</li>
        <li>Every order after this is signed here, with no wallet popup.</li>
        <li>You can revoke it from Sessions &amp; devices at any time.</li>
        {/*
          **The one thing this modal owes that is not about the key** (B8).
          Everything above is a promise about what a session key can do, and all
          of it is still true — but a user reading "it can never move your money"
          would reasonably infer that nobody can, and since the venue's own token
          (§F45) and the net settlement (§F94) that is not the case. One
          sentence, pointing at the screen that explains it, rather than a
          paragraph that turns a permission prompt into a disclosure.
        */}
        <li>
          Separately: UpTik works out what each market you trade moves in or out
          of your trading balance, can freeze an account it believes is
          manipulating the orderbook, and can upgrade the contract that settles
          it. See Balance.
        </li>
      </ul>

      {atBoundary ? (
        <p className="mt-3 rounded-sm bg-warning-bg px-2 py-1_5 text-caption text-warning-text">
          This market is about to settle. Signing now may take you past the start
          of the next one.
        </p>
      ) : null}

      {identity.keyNotice ? (
        <p className="mt-3 rounded-sm bg-info-bg px-2 py-1_5 text-caption text-info-text">
          {identity.keyNotice}
        </p>
      ) : null}
    </>
  )
}
